<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import OverflowMenu from '@/components/shared/OverflowMenu.vue'
import type { Invitation, InvitationStatus } from '@/types/team'

const props = defineProps<{
  organisationId: number
  invitations: Invitation[]
  isOwner: boolean
  busy?: boolean
}>()

const emit = defineEmits<{
  cancel: [invitationId: number]
  resend: [invitationId: number]
}>()

const { t } = useI18n()

// Open row menus keyed by invitation id, so a menu can be closed once an item is chosen.
const menuRefs = new Map<number, { close: () => void }>()

function setMenuRef(id: number, el: unknown): void {
  if (el && typeof el === 'object' && 'close' in el) {
    menuRefs.set(id, el as { close: () => void })
  } else {
    menuRefs.delete(id)
  }
}

function hasActions(invitation: Invitation): boolean {
  return invitation.status === 'pending' || invitation.status === 'expired'
}

function resend(invitation: Invitation): void {
  menuRefs.get(invitation.id)?.close()
  emit('resend', invitation.id)
}

function cancel(invitation: Invitation): void {
  menuRefs.get(invitation.id)?.close()
  emit('cancel', invitation.id)
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function statusBadgeClass(status: InvitationStatus): string {
  switch (status) {
    case 'pending':
      return 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
    case 'accepted':
      return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
    case 'cancelled':
      return 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
    case 'expired':
      return 'bg-red-500/20 text-red-400 border border-red-500/30'
  }
}

function roleBadgeClass(role: 'owner' | 'admin'): string {
  return role === 'owner'
    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
    : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
}

function invitedByName(invitation: Invitation): string {
  if (!invitation.invited_by) return '—'
  return `${invitation.invited_by.first_name} ${invitation.invited_by.last_name}`
}
</script>

<template>
  <div>
    <div
      v-if="invitations.length === 0"
      class="rounded-xl border border-dashed border-slate-700 px-8 py-12 text-center"
    >
      <p class="text-slate-400 text-sm">{{ t('team.invitations.empty_state') }}</p>
    </div>
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm text-left">
        <thead>
          <tr class="border-b border-slate-700">
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('team.invitations.column_email') }}
            </th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('team.invitations.column_role') }}
            </th>
            <th class="hidden md:table-cell px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('team.invitations.column_invited_by') }}
            </th>
            <th class="hidden md:table-cell px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('team.invitations.column_sent') }}
            </th>
            <th class="hidden lg:table-cell px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('team.invitations.column_expires') }}
            </th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('team.invitations.column_status') }}
            </th>
            <th v-if="isOwner" class="px-3 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
              {{ t('team.invitations.column_actions') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="invitation in invitations"
            :key="invitation.id"
            class="border-b border-slate-700/50 hover:bg-slate-800/50 transition-colors"
          >
            <!-- Email -->
            <td class="px-3 py-3 text-white truncate max-w-xs">
              {{ invitation.email }}
            </td>

            <!-- Role -->
            <td class="px-3 py-3">
              <span
                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                :class="roleBadgeClass(invitation.role)"
              >
                {{ t(`team.roles.${invitation.role}`) }}
              </span>
            </td>

            <!-- Invited by -->
            <td class="hidden md:table-cell px-3 py-3 text-slate-400">
              {{ invitedByName(invitation) }}
            </td>

            <!-- Sent date -->
            <td class="hidden md:table-cell px-3 py-3 text-slate-400">
              {{ formatDate(invitation.created_at) }}
            </td>

            <!-- Expires -->
            <td class="hidden lg:table-cell px-3 py-3 text-slate-400">
              {{ formatDate(invitation.expires_at) }}
            </td>

            <!-- Status -->
            <td class="px-3 py-3">
              <span
                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                :class="statusBadgeClass(invitation.status)"
              >
                {{ t(`team.invitations.status_${invitation.status}`) }}
              </span>
            </td>

            <!-- Actions -->
            <td v-if="isOwner" class="px-3 py-3 text-right">
              <div class="flex items-center justify-end">
                <OverflowMenu
                  :ref="(el) => setMenuRef(invitation.id, el)"
                  teleport
                  menu-class="w-40"
                  :disabled="!hasActions(invitation)"
                  :aria-label="hasActions(invitation)
                    ? t('team.invitations.row_actions')
                    : t('team.invitations.no_row_actions')"
                >
                  <button
                    type="button"
                    :disabled="busy"
                    class="w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    @click="resend(invitation)"
                  >
                    {{ t('team.invitations.resend_button') }}
                  </button>
                  <template v-if="invitation.status === 'pending'">
                    <hr class="border-slate-700 my-1" />
                    <button
                      type="button"
                      :disabled="busy"
                      class="w-full text-left px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-slate-700/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      @click="cancel(invitation)"
                    >
                      {{ t('team.invitations.cancel_button') }}
                    </button>
                  </template>
                </OverflowMenu>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
